import {useAccessToken} from "../../services/auth"
import React, {useRef, useState, PropsWithChildren, useEffect} from "react"
import useAsyncResultIdle from "../../hooks/useAsyncResultIdle"
import {useAsyncResult} from "../../hooks/useAsyncResult"
import Loading from "../../components/Loading/Loading"
import {CheckDeposit, getCheckDepositRequest, updateCheckDeposit} from "../../resources/checkDeposits"
import {AsyncResultIdleRequestState} from "../../types/asyncResultIdle"
import classNames from "classnames"
import Button from "../Button/Button"

const checkNumberHtmlPattern = "^[0-9-]*$"
const counterpartyAccountNumberHtmlPattern = "^[0-9-]*$"
const counterpartyRoutingNumberHtmlPattern = "^[0-9]{9}$"

type CloseFunc = () => void
type RefreshFunc = () => void

interface AsyncFormProps<T, E> {
	title: string
	classname?: string
	cancelButtonText: string
	buttonText: string
	buttonClassname: string
	cancelButtonClassname?: string
	state: AsyncResultIdleRequestState<T, E>
	onSubmit: Function
	onSuccess?: Function
	errorToText: (err: E) => string
	close: () => void
	buttonDisabled?: boolean
}

function FormHeader<T, E>(
	props: PropsWithChildren<AsyncFormProps<T, E>> & {
		cancelButtonClassname: string
		buttonClassname: string
		buttonDisabled?: boolean
	}
) {
	return (
		<header className="form-header">
			<span className="form-header-title">{props.title}</span>
			<div className="form-header-ctas">
				<Button
					type="button"
					theme="neutral"
					variant="subtle"
					className={props.cancelButtonClassname}
					disabled={props.buttonDisabled}
					onClick={props.close}
				>
					{props.cancelButtonText}
				</Button>
				<Button
					type="submit"
					prefixIcon="approve-v-check-validation"
					theme="success"
					variant="primary"
					className={props.buttonClassname}
					disabled={props.buttonDisabled}
				>
					{props.buttonText}
				</Button>
			</div>
		</header>
	)
}

function Idle<T, E>(props: PropsWithChildren<AsyncFormProps<T, E>>) {
	const buttonClassname = classNames("button", props.buttonClassname)
	const cancelButtonClassname = classNames("button", props.cancelButtonClassname)

	return (
		<>
			<FormHeader
				{...props}
				buttonClassname={buttonClassname}
				cancelButtonClassname={cancelButtonClassname}
				buttonDisabled={props.buttonDisabled}
			/>
			<section className="form-body">
				<fieldset>{props.children}</fieldset>
			</section>
		</>
	)
}

function Pending<T, E>(props: PropsWithChildren<AsyncFormProps<T, E>>) {
	const buttonClassname = classNames("button", "is-loading", props.buttonClassname)
	const cancelButtonClassname = classNames("button", props.cancelButtonClassname)
	return (
		<>
			<FormHeader
				{...props}
				buttonClassname={buttonClassname}
				cancelButtonClassname={cancelButtonClassname}
				buttonDisabled={true}
			/>
			<section className="form-body">
				<fieldset disabled>{props.children}</fieldset>
			</section>
		</>
	)
}

function Ok<T, E>(props: PropsWithChildren<AsyncFormProps<T, E> & {value: T}>) {
	useEffect(() => {
		props.onSuccess?.()
	}, [])

	return null
}

function Err<T, E>(props: PropsWithChildren<AsyncFormProps<T, E>> & {err: E}) {
	const buttonClassname = classNames("button", props.buttonClassname)
	const cancelButtonClassname = classNames("button", props.cancelButtonClassname)
	return (
		<>
			<FormHeader {...props} buttonClassname={buttonClassname} cancelButtonClassname={cancelButtonClassname} />
			<section className="form-body">
				<div className="has-text-danger is-align-self-center">{props.errorToText(props.err)}</div>
				<fieldset>{props.children}</fieldset>
			</section>
		</>
	)
}

function AsyncForm<T, E>(props: PropsWithChildren<AsyncFormProps<T, E>>) {
	const formRef = useRef<HTMLFormElement>(null)
	const content = props.state.match(
		() => <Idle {...props} />,
		() => <Pending {...props} />,
		(v) => <Ok value={v} {...props} />,
		(err) => <Err {...props} err={err} />
	)

	return (
		<form
			ref={formRef}
			onSubmit={(e) => {
				e.preventDefault()
				props.onSubmit()
			}}
		>
			{content}
		</form>
	)
}

export function UpdateCheckDepositDetailsForm({
	close,
	refreshFunc,
	checkDeposit,
	fieldClassName,
}: {
	close: CloseFunc
	refreshFunc: RefreshFunc
	checkDeposit: CheckDeposit
	fieldClassName?: string
}) {
	const accessToken = useAccessToken()
	const [state, update] = useAsyncResultIdle(updateCheckDeposit)
	const [amount, setAmount] = useState(`${checkDeposit.attributes.amount}`)
	const [counterpartyRoutingNumber, setCounterpartyRoutingNumber] = useState(
		checkDeposit.attributes.counterparty.routingNumber
	)
	const [counterpartyAccountNumber, setCounterpartyAccountNumber] = useState(
		checkDeposit.attributes.counterparty.accountNumber
	)
	const fieldClassNames = classNames(fieldClassName)

	const [checkNumber, setCheckNumber] = useState(checkDeposit.attributes.checkNumber)

	const [micr, setMICR] = useState(checkDeposit.attributes.micr)

	return (
		<AsyncForm
			title="Edit for X9"
			onSubmit={() =>
				update({
					accessToken,
					id: checkDeposit.id,
					amount: parseInt(amount),
					counterpartyRoutingNumber,
					counterpartyAccountNumber,
					checkNumber,
					micr,
				})
			}
			close={close}
			state={state}
			cancelButtonText="Cancel"
			buttonClassname="is-success"
			buttonText="Save"
			errorToText={(err) => (err.errors[0].detail ? err.errors[0].detail : err.errors[0].title)}
			onSuccess={refreshFunc}
		>
			<div className={fieldClassNames}>
				<label htmlFor="amount" className="label">
					Check Amount (in cents)
				</label>
				<div className="control">
					<input
						id="amount"
						className="input"
						type="text"
						placeholder="Check Amount"
						value={amount}
						pattern="^[0-9]*$"
						required
						onChange={(e) => setAmount(e.target.value)}
					/>
				</div>
			</div>
			<div className={fieldClassNames}>
				<label htmlFor="checkNumber" className="label">
					Check Number
				</label>
				<div className="control">
					<input
						id="checkNumber"
						className="input"
						type="text"
						placeholder="Check Number"
						value={checkNumber}
						pattern={checkNumberHtmlPattern}
						required
						onChange={(e) => setCheckNumber(e.target.value)}
					/>
				</div>
			</div>
			<div className={fieldClassNames}>
				<label htmlFor="counterpartyRoutingNumber" className="label">
					Counterparty Routing #
				</label>
				<div className="control">
					<input
						id="counterpartyRoutingNumber"
						className="input"
						type="text"
						placeholder="Counterparty Routing #"
						value={counterpartyRoutingNumber}
						pattern={counterpartyRoutingNumberHtmlPattern}
						required
						onChange={(e) => setCounterpartyRoutingNumber(e.target.value)}
					/>
				</div>
			</div>
			<div className={fieldClassNames}>
				<label htmlFor="counterpartyAccountNumber" className="label">
					Counterparty Account #
				</label>
				<div className="control">
					<input
						id="counterpartyAccountNumber"
						className="input"
						type="text"
						placeholder="Counterparty Account #"
						value={counterpartyAccountNumber}
						pattern={counterpartyAccountNumberHtmlPattern}
						required
						onChange={(e) => setCounterpartyAccountNumber(e.target.value)}
					/>
				</div>
			</div>
			<div className={fieldClassNames}>
				<label htmlFor="micr" className="label">
					MICR
				</label>
				<div className="control">
					<input
						id="micr"
						className="input"
						type="text"
						placeholder="MICR"
						value={micr}
						pattern="^[0-9A-Za-z\s]*$"
						required
						onChange={(e) => setMICR(e.target.value)}
					/>
				</div>
			</div>
		</AsyncForm>
	)
}

export default function UpdateCheckDepositDetails({
	checkDepositId,
	refreshFunc,
	refreshToken,
	close,
	fieldClassName,
}: {
	checkDepositId: string
	close: CloseFunc
	refreshToken: number
	refreshFunc: RefreshFunc
	fieldClassName?: string
}) {
	const accessToken = useAccessToken()

	const checkDepositAsync = useAsyncResult(
		() => getCheckDepositRequest({accessToken, id: checkDepositId}),
		[refreshToken]
	)

	return checkDepositAsync.match(
		() => <Loading />,
		(checkDeposit) => (
			<UpdateCheckDepositDetailsForm
				checkDeposit={checkDeposit.data}
				close={close}
				refreshFunc={refreshFunc}
				fieldClassName={fieldClassName}
			/>
		),
		(_) => null
	)
}
